/**
 * Shared fuzzy-search helpers.
 *
 * Every search box in the site (header site search, FAQs, programme
 * filters, programme page search) runs through these helpers so a typo
 * like "diplona" or "acomodation" still finds the right result.
 *
 * Scoring: exact substring hits rank highest (start-of-string > word
 * boundary > mid-word), then Levenshtein-close words within a small
 * edit budget that grows with the token length.
 */

export const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

export function tokenize(term) {
  return normalize(term).split(/\s+/).filter(Boolean);
}

export function levenshteinDistance(left, right, limit = 3) {
  if (Math.abs(left.length - right.length) > limit) return limit + 1;
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);

  for (let row = 1; row <= left.length; row += 1) {
    const current = [row];
    let rowMinimum = row;

    for (let column = 1; column <= right.length; column += 1) {
      const cost = left[row - 1] === right[column - 1] ? 0 : 1;
      const value = Math.min(
        current[column - 1] + 1,
        previous[column] + 1,
        previous[column - 1] + cost
      );
      current[column] = value;
      rowMinimum = Math.min(rowMinimum, value);
    }

    if (rowMinimum > limit) return limit + 1;
    previous = current;
  }

  return previous[right.length];
}

/** How many edits we tolerate for a token of this length. */
export function editBudget(token) {
  if (token.length >= 8) return 3;
  if (token.length >= 6) return 2;
  if (token.length >= 4) return 1;
  return 0;
}

function fuzzyTokenScore(token, words) {
  const maxDistance = editBudget(token);
  if (!maxDistance) return 0;

  return words.reduce((best, word) => {
    // Prefix typo tolerance: "accomodatio" vs "accommodation".
    const trimmed = word.length > token.length ? word.slice(0, token.length + maxDistance) : word;
    const distance = Math.min(
      levenshteinDistance(token, word, maxDistance),
      levenshteinDistance(token, trimmed, maxDistance)
    );
    if (distance > maxDistance) return best;
    return Math.max(best, 3 - distance);
  }, 0);
}

/** Score a haystack against pre-tokenized search terms. 0 = no match. */
export function scoreMatch(haystack, tokens) {
  const text = normalize(haystack);
  if (!text || !tokens.length) return 0;
  const words = text.split(/[^a-z0-9]+/).filter(Boolean);
  let score = 0;

  for (const token of tokens) {
    const index = text.indexOf(token);
    if (index !== -1) {
      if (index === 0) score += 6;
      else if (/[^a-z0-9]/.test(text[index - 1] || "")) score += 4;
      else score += 2;
      continue;
    }

    const fuzzyScore = fuzzyTokenScore(token, words);
    if (!fuzzyScore) return 0; // every token must match somehow
    score += fuzzyScore;
  }

  return score;
}

/** Convenience boolean matcher for simple filter callbacks. */
export function fuzzyMatches(haystack, term) {
  const tokens = tokenize(term);
  if (!tokens.length) return true;
  return scoreMatch(haystack, tokens) > 0;
}

/**
 * Rank a list by fuzzy relevance.
 * `getText` returns the searchable text; `getTitle` (optional) is
 * weighted double so title hits float to the top.
 */
export function fuzzyRank(items, term, getText, getTitle) {
  const tokens = tokenize(term);
  if (!tokens.length) return items;

  return items
    .map((item) => {
      const base = scoreMatch(getText(item), tokens);
      const title = getTitle ? scoreMatch(getTitle(item), tokens) : 0;
      return { item, score: base + title * 2 };
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((row) => row.item);
}
